import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroAsset from "@/assets/blog/mag-forms.jpg.asset.json";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/magnesium-glycinate-vs-citrate-india";
const TITLE = "Magnesium Bisglycinate vs Citrate vs Chloride: India Buyer's Guide (2026) | TruWe";

export const Route = createFileRoute("/blog/magnesium-glycinate-vs-citrate-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "magnesium-glycinate-vs-citrate-india",
      title: TITLE,
      description:
        "Which magnesium form is best in India, bisglycinate, citrate, chloride, oxide or aspartate? Bioavailability, uses, side-effects and how to read a supplement label.",
      ogTitle: "Magnesium Forms Compared: Bisglycinate vs Citrate vs Chloride",
      ogDescription:
        "A no-nonsense India buyer's guide to magnesium forms, what actually gets absorbed, what's for sleep, what's for the gut.",
      headline: "Magnesium Bisglycinate vs Citrate vs Chloride. India Buyer's Guide",
      breadcrumbName: "Magnesium Forms Compared (India Buyer's Guide)",
      datePublished: "2026-06-28",
      dateModified: "2026-07-25",
      ogImage: `https://truwe.in${heroAsset.url}`,
      faqs: [
        {
          q: "Which magnesium form is best for sleep and anxiety?",
          a: "Magnesium bisglycinate. The glycine carrier is itself a calming amino acid and the chelate has one of the highest oral bioavailability profiles with minimal laxative effect.",
        },
        {
          q: "Is magnesium oxide worth buying?",
          a: "Not really. Elemental magnesium is high on paper (~60%) but real absorption is closer to 4%. It's cheap because it barely works, most of it acts as an osmotic laxative.",
        },
        {
          q: "Can I take different magnesium forms together?",
          a: "Yes, and this is what a well-formulated stack does. Different forms hit different tissues: bisglycinate for the nervous system, chloride/aspartate for cellular ATP, Aquamin® for structural minerals.",
        },
        {
          q: "How much elemental magnesium do Indian adults actually need?",
          a: "The RDA is 310-420 mg/day depending on age and sex. Most Indian diets deliver 180-250 mg because refined grains and low-magnesium soil have thinned out food levels. A 200-300 mg top-up is typical.",
        },
      ],
    }),
});

const FORMS = [
  {
    name: "Magnesium Bisglycinate",
    absorption: "Very high (~80%+)",
    bestFor: "Sleep, anxiety, muscle tension, PMS",
    note: "Chelated to two glycine molecules. Glycine is itself calming and pro-sleep. Gentle on the gut, the go-to for daily use.",
  },
  {
    name: "Magnesium Chloride",
    absorption: "High",
    bestFor: "Cellular energy, cramps, low stomach acid",
    note: "Ionic and well-absorbed even when stomach acid is low (common on antacids or after 40). Slightly salty taste in solution.",
  },
  {
    name: "Magnesium Aspartate",
    absorption: "High",
    bestFor: "ATP production, endurance, fatigue",
    note: "Aspartic acid feeds directly into the Krebs cycle. Pairs well with malate for daytime energy.",
  },
  {
    name: "Magnesium from Aquamin® (marine)",
    absorption: "High (natural matrix)",
    bestFor: "Bone density, mineral cofactors",
    note: "Multi-mineral marine source (red algae), magnesium plus 72 trace minerals. Structural, not sedating.",
  },
  {
    name: "Magnesium Citrate",
    absorption: "Moderate-high",
    bestFor: "Occasional constipation",
    note: "Well-absorbed but osmotic, pulls water into the bowel. Great for occasional use, poor for daily calm because it can loosen stools.",
  },
  {
    name: "Magnesium Oxide",
    absorption: "Poor (~4%)",
    bestFor: "Cheap laxative, little else",
    note: "The 'magnesium' in most sub-₹300 Indian bottles. Looks good on the label (high elemental %), barely gets past the gut wall.",
  },
  {
    name: "Magnesium L-Threonate",
    absorption: "Moderate, brain-specific",
    bestFor: "Cognition, focus",
    note: "One of the few forms shown to cross the blood-brain barrier in animal studies. Expensive and niche, not needed for general sleep or cramps.",
  },
] as const;

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Magnesium
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Magnesium bisglycinate vs citrate vs chloride: an India buyer's guide
          </h1>
          <BlogByline date="2026-06-28" readingTime="9 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            The Indian supplement aisle is stacked with magnesium bottles that all claim
            "500 mg high-strength". Most of them are magnesium oxide, cheap, high on the
            label, and 96% of it never crosses your gut wall. Here is what actually
            matters, form by form, and how to read a real label.
          </p>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset.url}
              alt="Magnesium bisglycinate, chloride and aspartate tablets compared. India buyer's guide"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Why the form on the label matters more than the mg number
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            "Magnesium 500 mg" tells you almost nothing. What matters is (a) which
            chemical form bisglycinate, citrate, chloride, oxide, aspartate and
            (b) how much <em>elemental</em> magnesium your body can actually absorb. A
            250 mg dose of bisglycinate typically delivers more usable magnesium than
            500 mg of oxide.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The seven forms, ranked honestly
          </h2>
          <div className="mt-5 space-y-4">
            {FORMS.map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-1 text-sm font-medium text-brand">
                  Absorption: {row.absorption} · Best for: {row.bestFor}
                </p>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Why a stack beats a single form
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            Magnesium isn't stored much, it's used and turned over. Different tissues
            prefer different carriers. Bisglycinate is best for the nervous system;
            chloride and aspartate feed cellular ATP; Aquamin® delivers a food-matrix
            mineral profile that supports bone. A well-designed stack covers all three
            without stacking three separate bottles.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What to avoid on Indian labels
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>"Magnesium 500 mg"</strong> with no form named, almost always oxide.
            </li>
            <li>
              <strong>"Proprietary blend"</strong> that hides how many mg of each form. If
              they won't tell you the dose, assume it's a pinch.
            </li>
            <li>
              <strong>Magnesium stearate</strong> listed as the active, it's a flow agent,
              not a source of magnesium.
            </li>
            <li>
              <strong>No third-party test report</strong> heavy-metal contamination
              (lead, arsenic) is a real risk in mineral supplements sourced cheaply.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Mag Absorb Pro, five forms, one tablet
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Mag Absorb Pro combines <strong>Aquamin® marine magnesium, bisglycinate,
              chloride, aspartate and gluconate</strong> the five forms that between them cover
              sleep, cellular energy and bone. No oxide. Third-party tested. Made in a
              USFDA / GMP / FSSAI facility.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Five absorbable forms" },
                { icon: ShieldCheck, label: "Heavy-metal tested" },
                { icon: Beaker, label: "USFDA / GMP / FSSAI facility" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "mag-absorb-pro" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Mag Absorb Pro <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="magnesium" />
          <ClusterLinks cluster="magnesium" />
          <AuthorityFlow cluster="magnesium" />
          <References slug="magnesium-glycinate-vs-citrate-india" />
          <RelatedReading articles={relatedArticles("magnesium-glycinate-vs-citrate-india")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            For general education only. Speak to a qualified clinician before starting
            any supplement, particularly if you take medication or have a kidney condition.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
